import type { PostingKeyword } from "@/lib/api/catalogue";
import { says } from "./wholeWord";

/** How much of a job's keywords a resume's text names. */
export interface ToolFit {
    /** The keywords the job names. */
    asked: number;
    covered: number;
    /** `covered` over `asked`, from 0 to 1, and 0 when the job names none. */
    share: number;
    /** The covered keywords' terms, in the order the job names them. */
    coveredTerms: string[];
    /** The terms of those the resume does not name, in the same order. */
    missing: string[];
}

/** Whether the text names the keyword, by its term or by any spelling of it. */
function names(resume: string, keyword: PostingKeyword): boolean {
    return [keyword.term, ...(keyword.spellings ?? [])].some((spelling) => says(resume, spelling));
}

/** The keywords a resume's text names and those it does not, each once, in the job's order. */
export function toolFit(keywords: readonly PostingKeyword[], resume: string): ToolFit {
    const coveredTerms: string[] = [];
    const missing: string[] = [];
    for (const keyword of keywords) {
        (names(resume, keyword) ? coveredTerms : missing).push(keyword.term);
    }
    const asked = keywords.length;
    return {
        asked,
        covered: coveredTerms.length,
        share: asked === 0 ? 0 : coveredTerms.length / asked,
        coveredTerms,
        missing,
    };
}

/** A text with each run of white space cut to one space, trimmed, in lower case. */
function squash(text: string): string {
    return text.replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * Whether the resume box holds the job's own text, pasted twice by
 * mistake: the same words, whatever the spacing and case. Two empty
 * texts are not the same, there is nothing to say.
 */
export function sameText(job: string, resume: string): boolean {
    const squashed = squash(job);
    return squashed !== "" && squashed === squash(resume);
}
